/**
 * InfraCoinn — Deployed contracts on Polygon Mainnet
 * Verified on https://polygonscan.com
 */

export const INFRACOINN_ADDRESSES = {
  OMX_TOKEN:     '0x65BeC106AB57253CC9fB9DE3Aefc244930882e58', // InfracoinnToken (OMX)
  TIMELOCK:      '0x2d658E0490552D0a2588B8d23579B7F99947DF59', // InfracoinnTimelock
  TREASURY:      '0x026a969Ba54c5183Fd534A06B8e7f0e2731E1CfE', // InfracoinnTreasury
  RWA:           '0xA270C84DCea4210BDC7c3c32E9f7dcB3797B08df', // InfracoinnRWA (ERC-1155)
  RWA_FACTORY:   '0xAcF6FE7E7f35D01154556980570EA3be54D381b6', // InfracoinnRWAFactory ← use this to deploy
  STAKING:       '0x9C44e53f7f0Dbb1bb3c731DF2a6Da1a10B12B366', // InfracoinnStaking
  GUARANTEE:     '0x54Eaa2C945140199161CD656AcEa59FE9e6AA1A4', // InfracoinnGuarantee
  INSTRUMENT:    '0x01CF7e9A7a394Bc695f8eE347D88241A3f9948d1', // InfracoinnInstrument
  AFFILIATE:     '0x74b213ea262a536F683EbDAEc3d8f9815Ddb4F42', // InfracoinnAffiliate
  GOVERNANCE:    '0x1E0CAa967B37A0a123DF4fE4ed8f2fD0e4338fbe', // InfracoinnGovernance
  MARKETPLACE:   '0x07A32B329Bb098665A7356213a9A2386a2adE66b', // InfracoinnMarketplace
} as const;

export const POLYGON_CHAIN_ID = 137;
export const POLYGON_RPC = 'https://polygon-rpc.com';
export const POLYGONSCAN_BASE = 'https://polygonscan.com';

export type InfracoinnContractKey = keyof typeof INFRACOINN_ADDRESSES;

export const CONTRACT_LABELS: Record<InfracoinnContractKey, { name: string; description: string; icon: string }> = {
  OMX_TOKEN:   { name: 'OMX Token',        description: 'Token nativo de InfraCoinn (ERC-20)',           icon: '🪙' },
  TIMELOCK:    { name: 'Timelock',          description: 'Controlador de tiempos de gobernanza',          icon: '⏱️' },
  TREASURY:    { name: 'Treasury',          description: 'Tesorería del protocolo',                       icon: '🏦' },
  RWA:         { name: 'RWA Token',         description: 'Contrato principal de activos reales (ERC-1155)', icon: '🏢' },
  RWA_FACTORY: { name: 'RWA Factory',       description: 'Factory para desplegar nuevos activos tokenizados', icon: '🏭' },
  STAKING:     { name: 'Staking',           description: 'Staking de tokens OMX',                        icon: '💎' },
  GUARANTEE:   { name: 'Guarantee',         description: 'Fondo de garantía para inversores',             icon: '🛡️' },
  INSTRUMENT:  { name: 'Instrument',        description: 'Instrumentos financieros tokenizados',          icon: '📊' },
  AFFILIATE:   { name: 'Affiliate',         description: 'Programa de afiliados on-chain',                icon: '🤝' },
  GOVERNANCE:  { name: 'Governance',        description: 'Votación y propuestas de gobernanza',           icon: '🗳️' },
  MARKETPLACE: { name: 'Marketplace',       description: 'Marketplace de activos tokenizados',            icon: '🛒' },
};

// ── Minimal ABIs (only the functions ZAPHUB needs) ──────────────────────────

export const ABI_ERC20_MINIMAL = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
] as const;

export const ABI_RWA_FACTORY = [
  // Create a new RWA token (ERC-1155 asset)
  'function createAsset(string name, string symbol, uint256 totalSupply, string metadataURI, address owner) returns (uint256 assetId)',
  'function createERC20Asset(string name, string symbol, uint256 totalSupply, address owner) returns (address tokenAddress)',
  // Read
  'function getAssetCount() view returns (uint256)',
  'function getAsset(uint256 assetId) view returns (address contractAddr, string name, string symbol, address owner, bool certified)',
  'function getAllAssets() view returns (tuple(address contractAddr, string name, string symbol, address owner, bool certified)[])',
  // Events
  'event AssetCreated(uint256 indexed assetId, address indexed contractAddr, address indexed owner, string name, string symbol)',
] as const;

export const ABI_RWA = [
  'function uri(uint256 id) view returns (string)',
  'function balanceOf(address account, uint256 id) view returns (uint256)',
  'function totalSupply(uint256 id) view returns (uint256)',
  'function mint(address to, uint256 id, uint256 amount, bytes data)',
  'function certify(uint256 assetId)',
  'function isCertified(uint256 assetId) view returns (bool)',
  'event AssetMinted(uint256 indexed assetId, address indexed to, uint256 amount)',
  'event AssetCertified(uint256 indexed assetId)',
] as const;

export const ABI_STAKING = [
  'function stake(uint256 amount)',
  'function unstake(uint256 amount)',
  'function claimRewards()',
  'function stakedBalance(address) view returns (uint256)',
  'function pendingRewards(address) view returns (uint256)',
  'function totalStaked() view returns (uint256)',
  'function apr() view returns (uint256)',
  'event Staked(address indexed user, uint256 amount)',
  'event Unstaked(address indexed user, uint256 amount)',
  'event RewardsClaimed(address indexed user, uint256 amount)',
] as const;

export const ABI_GOVERNANCE = [
  'function propose(address[] targets, uint256[] values, bytes[] calldatas, string description) returns (uint256)',
  'function castVote(uint256 proposalId, uint8 support)',
  'function state(uint256 proposalId) view returns (uint8)',
  'function proposalVotes(uint256 proposalId) view returns (uint256 againstVotes, uint256 forVotes, uint256 abstainVotes)',
  'event ProposalCreated(uint256 indexed proposalId, address proposer, string description)',
  'event VoteCast(address indexed voter, uint256 indexed proposalId, uint8 support, uint256 weight)',
] as const;

export const ABI_MARKETPLACE = [
  'function listAsset(uint256 assetId, uint256 pricePerToken, uint256 amount)',
  'function buyAsset(uint256 listingId, uint256 amount) payable',
  'function cancelListing(uint256 listingId)',
  'function getListing(uint256 listingId) view returns (uint256 assetId, address seller, uint256 pricePerToken, uint256 amount, bool active)',
  'function getActiveListings() view returns (uint256[])',
  'event AssetListed(uint256 indexed listingId, uint256 indexed assetId, address indexed seller, uint256 pricePerToken)',
  'event AssetSold(uint256 indexed listingId, address indexed buyer, uint256 amount)',
] as const;
